import { action, computed, observable } from 'mobx';
import isEmpty from 'lodash/isEmpty';
import type { RootStore } from '~/stores/root.store';
import { DnDTypes, TBlock, TServiceItemBlock } from '~/types/builder.types';
import { AutoPayStatus, IInvoiceBlockResponse } from '~/types/server.types';
import { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import type { InvoiceColumnHeaderBlock } from '~/models/blocks/invoice-column-header-block.model';
import type { InvoiceHeaderBlock } from '~/models/blocks/invoice-header-block.model';
import type { InvoiceSummaryBlock } from '~/models/blocks/invoice-summary-block.model';
import type { PaymentScheduleBlock } from '~/models/blocks/payment-schedule-block.model';
import type { ServicesBlock } from '~/models/blocks/services-block.model';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { ServiceSelectionBlock } from '~/models/blocks/service-selection-block.model';
import {
    TInvoiceType,
    TInvoiceTypes,
    ServiceSelectionType
} from '~/types/model.types';
import type { ServiceItemBlock } from '~/models/blocks/service-item-block.model';
import type { RecurringScheduleBlock } from '~/models/blocks/recurring-schedule-block.model';
import { MobileContainerName } from '~/components/Builder/Block/ServicesBlock/Mobile/MobileInvoice.constants';

type InvoiceBlockDataForServer = ReturnType<
    ContainerableBlock['getDataForServer']
> &
    Pick<IInvoiceBlockResponse, 'invoice_type' | 'type'>;

export class InvoiceBlock extends ContainerableBlock {
    type: 'invoice_container_block';
    @observable invoice_type: TInvoiceType;
    @observable sub_total: number;
    @observable total_tax: number;
    @observable total_discount: number;
    @observable total_price: number;

    constructor(
        block: IInvoiceBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.invoice_type = block.invoice_type || 'payment_schedule';
    }

    @action
    setInvoiceType(invoiceType) {
        this.invoice_type = invoiceType;
    }

    get hoverable(): boolean {
        const {
            userStore: { currUser }
        } = this.rootStore;

        return !currUser.isSimplifiedInvoiceEnabled;
    }

    get isDeletable() {
        return true;
    }

    @computed
    get invoiceHeaderBlock() {
        return this.childBlocks.find(
            block => block.type === 'invoice_header_block'
        ) as InvoiceHeaderBlock;
    }

    @computed
    get invoiceColumnHeaderBlock() {
        return this.childBlocks.find(
            block => block.type === 'invoice_column_header_block'
        ) as InvoiceColumnHeaderBlock;
    }

    @computed
    get invoiceSummaryBlock() {
        return this.childBlocks.find(
            block => block.type === 'invoice_summary_block'
        ) as InvoiceSummaryBlock;
    }

    @computed
    get cogIconTestId() {
        return MobileContainerName.InvoiceContainer;
    }

    @computed
    get invoicePaymentScheduleBlock() {
        return this.childBlocks.find(
            block => block.type === 'payment_schedule_block'
        ) as PaymentScheduleBlock;
    }

    @computed
    get invoiceRecurringScheduleBlock(): RecurringScheduleBlock {
        return this.childBlocks.find(
            block => block.type === 'recurring_schedule_block'
        ) as RecurringScheduleBlock;
    }

    @computed
    get scheduleBlock() {
        if (this.isRecurringInvoice) {
            return this.invoiceRecurringScheduleBlock;
        }

        return this.invoicePaymentScheduleBlock;
    }

    @computed
    get invoiceServicesBlock() {
        return this.childBlocks.find(
            block => block.type === 'services_block'
        ) as ServicesBlock;
    }

    @computed
    get chargeBlock() {
        return this.flowRevision.chargeBlock;
    }

    @computed
    get isRecurringUnder10Days() {
        if (this.isRecurringInvoice) {
            const {
                repetition_unit_type,
                custom_unit_count,
                custom_unit_type
            } = this.invoiceRecurringScheduleBlock.recurring_schedule;
            return (
                repetition_unit_type === 'daily' ||
                repetition_unit_type === 'weekly' ||
                (repetition_unit_type === 'custom' &&
                    custom_unit_type === 'days' &&
                    custom_unit_count < 11) ||
                (repetition_unit_type === 'custom' &&
                    custom_unit_type === 'weeks' &&
                    custom_unit_count < 2)
            );
        }
        return false;
    }

    @computed
    get shouldAddCompositionMenu() {
        return true;
    }

    @computed
    get allMainServiceItemBlocks() {
        return [
            ...this.clientSelectedServiceItemsBlocks,
            ...this.invoiceFixedItems
        ];
    }

    @computed
    get invoiceFixedItems(): ServiceItemBlock[] {
        return [
            ...this.autoSelectedServiceItemsBlocks,
            ...this.invoiceServicesBlock.childBlocks
        ];
    }

    @computed
    get hasMultipleServicesOrSubItems() {
        const somePackageHasMultipleServices =
            this.invoiceServicesBlock.childBlocks?.some(
                (service: TServiceItemBlock) => {
                    return service.childBlocks.length > 1;
                }
            );
        return (
            this.invoiceServicesBlock.childBlocks?.length > 1 ||
            somePackageHasMultipleServices
        );
    }

    @computed
    get hasFixedItems() {
        return !isEmpty(this.invoiceFixedItems);
    }

    @computed
    get isBeforeClientSelectedServices() {
        return (
            this.hasSelection && isEmpty(this.clientSelectedServiceItemsBlocks)
        );
    }

    @computed
    get hasSelection() {
        return this.invoiceServiceSelectionBlocks.some(selectionBlock => {
            return (
                selectionBlock.selection_type !==
                ServiceSelectionType.NoSelection
            );
        });
    }

    @computed
    get hasIncompleteServiceSelections() {
        return this.invoiceServiceSelectionBlocks.some(
            block => !block.isSelectionComplete
        );
    }

    @computed
    get autoSelectedServiceItemsBlocks() {
        return this.invoiceServiceSelectionBlocks
            .filter(
                selectionBlock =>
                    !selectionBlock.hasSelection &&
                    selectionBlock.send_info_to_invoice
            )
            .flatMap(selectionBlock => selectionBlock.serviceItems);
    }

    // TODO : refactor this function
    /* This function returns the items to be shown on invoice block */
    @computed
    get clientSelectedServiceItemsBlocks() {
        const allSelectedServices: ServiceItemBlock[] = [];
        this.invoiceServiceSelectionBlocks
            .filter(selectionBlock => selectionBlock.hasSelection)
            .forEach(serviceSelectionBlock => {
                allSelectedServices.push(
                    ...serviceSelectionBlock.selectedServiceBlocks
                );
            });
        return allSelectedServices.reduce(
            (servicesForInvoice: ServiceItemBlock[], selectedServiceItem) => {
                servicesForInvoice.push(selectedServiceItem);
                return servicesForInvoice;
            },
            []
        );
    }

    @computed
    get clientSelectedServiceItemsBlocksWithSubItems() {
        return this.clientSelectedServiceItemsBlocks.reduce(
            (serviceItemBlocks, serviceItemBlock) => [
                ...serviceItemBlocks,
                ...serviceItemBlock.subItems
            ],
            this.clientSelectedServiceItemsBlocks
        );
    }

    @computed
    get invoiceServiceSelectionBlocks() {
        return this.flowRevision.invoiceServiceSelectionBlocks(
            this.block_id
        ) as ServiceSelectionBlock[];
    }

    @computed
    get requiredInvoiceServiceSelectionBlocks() {
        return this.invoiceServiceSelectionBlocks.filter(
            serviceSelectionBlock => serviceSelectionBlock.selection_required
        );
    }

    get hasRequiredSelection() {
        return !!this.requiredInvoiceServiceSelectionBlocks.length;
    }

    @computed
    get hasServicesSelectionBlocks() {
        return this.invoiceServiceSelectionBlocks.length > 0;
    }

    @computed
    get isDeleteDisabled() {
        return this.scheduleBlock?.isDeleteDisabled || false;
    }

    get isActionBlock() {
        return true;
    }

    get isResponsesActionBlock() {
        return true;
    }

    get displayName() {
        return 'invoice';
    }

    @computed
    get hasPaidPayments() {
        return this.flowRevision.isSomePaymentPaid;
    }

    @computed
    get hasPendingPayments() {
        return this.invoicePaymentScheduleBlock?.hasPendingPayments;
    }

    @computed
    get isCutDisabled() {
        return this.hasPaidPayments;
    }

    @computed
    get hasNonManualPaidPayments() {
        return this.invoicePaymentScheduleBlock?.hasNonManualPaidPayments;
    }

    @computed
    get hasManualPaidPayments() {
        return this.invoicePaymentScheduleBlock?.hasManualPaidPayments;
    }

    @computed
    get isServiceSelectionsComplete() {
        return this.invoiceServiceSelectionBlocks?.every(
            serviceSelection => serviceSelection.isComplete
        );
    }

    @computed
    get isServicesSelectionFinal() {
        return !this.hasSelection || this.hasPaidPayments;
    }

    @computed
    get isPaymentAmountKnown() {
        return (
            this.isServicesSelectionFinal &&
            !this.invoiceSummaryBlock.isPendingClientPaymentSelection
        );
    }

    @computed
    get isClientPaymentPlan() {
        return this.invoiceSummaryBlock.isClientPaymentPlan;
    }

    @computed
    get cheapestSelectablePrice() {
        const minimalPriceOfEachSelection =
            this.requiredInvoiceServiceSelectionBlocks.map(
                ss => ss.cheapestServiceItem?.serviceMinPrice
            );

        return minimalPriceOfEachSelection.reduce((a, b) => a + b, 0);
    }

    isDraggable() {
        return true;
    }

    @computed
    get dndType() {
        return DnDTypes.HorizontalBlock;
    }

    @computed
    get numberOfPaymentsPaid() {
        if (
            this.invoice_type === 'payment_schedule' &&
            this.invoicePaymentScheduleBlock
        ) {
            return this.invoicePaymentScheduleBlock?.numberOfPaymentsPaid;
        } else if (
            this.invoice_type === 'recurring' &&
            this.invoiceRecurringScheduleBlock
        ) {
            return this.invoiceRecurringScheduleBlock?.paid_payments_count;
        }
    }

    dndAcceptedTypes() {
        return DnDTypes.HorizontalBlock;
    }

    @computed
    get shouldExpandOnSelect() {
        return false;
    }

    @computed
    get isRecurringInvoice() {
        return this.invoice_type === TInvoiceTypes.Recurring;
    }

    @computed
    get autoPayStatus() {
        return this.chargeBlock?.connectedVariable?.value?.auto_payment?.status;
    }

    @computed
    get isAutoPayRequired() {
        return this.chargeBlock?.allow_autopay;
    }

    @computed
    get showAutoPayBanner() {
        const paymentScheduleBlock = this.isRecurringInvoice
            ? this.invoiceRecurringScheduleBlock
            : this.invoicePaymentScheduleBlock;
        return (
            !!paymentScheduleBlock?.nextPaymentBlock &&
            !!this.autoPayStatus &&
            this.autoPayStatus !== AutoPayStatus.Inactive &&
            this.didClientActivateAutoPay
        );
    }

    @computed
    get didClientActivateAutoPay() {
        return (
            this.chargeBlock?.isAutoPayActive &&
            !this.chargeBlock?.connectedVariable?.value?.auto_payment
                ?.activated_by_vendor
        );
    }

    @computed
    get hasRecurringOrScheduledPaidPayments() {
        return this.isRecurringInvoice
            ? this.invoiceRecurringScheduleBlock?.paid_payments_count > 0
            : this.invoicePaymentScheduleBlock?.numberOfPaymentsPaid > 0;
    }

    @computed
    override get coupledBlocks(): TBlock[] {
        return [this.flow.payContainerBlock];
    }

    canBeParentOf(block: TBlock) {
        return [
            'invoice_header_block',
            'invoice_column_header_block',
            'invoice_summary_block',
            'payment_schedule_block',
            'services_block'
        ].includes(block.type);
    }

    getDataForServer(): InvoiceBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'invoice_container_block',
            invoice_type: this.invoice_type
        };
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);

        if (
            'connected_variable_id' in attributes &&
            block.connected_variable_id
        ) {
            this.connected_variable_id = block.connected_variable_id;
        }
    }
}
